/**
 * @fileoverview File upload modal for system-web — uploads to Azure Blob Storage.
 * @module system-web/components/FileUploadModal
 *
 * Responsibilities: - Pick a file, enforce type/size guardrails, upload it via the
 *                     inherently-online file-upload endpoint, and hand the created
 *                     UploadedFile back to the caller.
 *                   - Logo pickers pass an image-only type set + a small size rail
 *                     (LOGO_ALLOWED_TYPES / LOGO_MAX_SIZE_BYTES) and an inline image
 *                     preview; the generic document picker keeps the broad defaults.
 * Dependencies:     - apiPostFormData (auth-aware multipart POST — file upload is an
 *                     explicitly allowed inherently-online exception to local-first).
 * Architecture:     - Mirrors tenant-web's FileUploadModal signature so both portals
 *                     share one upload UX/guardrail contract (DRY across apps).
 * Notes:            - The backend writes the MongoDB record; replication syncs it to
 *                     Dexie. We do NOT write Dexie here (would create a duplicate).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Upload, FileUp, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { apiPostFormData } from '../apiClient';
import type { UploadedFileLocal } from '@firesquirrel/local-db';

/**
 * @contract FileUploadModalProps
 * Purpose: Inputs for the upload modal, including optional logo-scoped guardrails.
 * Fields:  isOpen/onClose — visibility; onSuccess — receives the uploaded file;
 *          allowedTypes/maxSizeBytes — type/size rails; category — locks + hides the
 *          category selector; title/subtitle/acceptHint — copy; showImagePreview —
 *          render an inline preview for image (logo) uploads.
 * Rules:   When `category` is provided the selector is hidden and that category is sent.
 * Sync:    n/a (presentation; upload is inherently online).
 */
interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (file: UploadedFileLocal) => void;
  /**
   * Restrict the accepted MIME types. Defaults to the broad document set.
   * Logo pickers pass an image-only set to enforce header-logo guardrails.
   */
  allowedTypes?: string[];
  /** Max upload size in bytes. Defaults to 50MB; logos use a small rail. */
  maxSizeBytes?: number;
  /** Lock the upload category (hides the category selector when provided). */
  category?: string;
  /** Modal heading. */
  title?: string;
  /** Modal subheading. */
  subtitle?: string;
  /** Show an inline image preview of the selected file (logo pickers). */
  showImagePreview?: boolean;
  /** Human-readable hint shown under the drop zone (e.g. accepted types/size). */
  acceptHint?: string;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

const DEFAULT_ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const DEFAULT_MAX_SIZE_BYTES = 50 * 1024 * 1024;

/** Image MIME types appropriate for a header logo. */
export const LOGO_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
/** Header-logo upload size rail (2MB). */
export const LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024;

const CATEGORY_OPTIONS = [
  { value: 'pamphlet', label: 'Pamphlet' },
  { value: 'instruction', label: 'Instructions' },
  { value: 'feeSchedule', label: 'Fee Schedule' },
  { value: 'letterhead', label: 'Letterhead' },
  { value: 'other', label: 'Other' },
];

/**
 * @component FileUploadModal
 * Purpose:     Upload a file (document or logo image) to blob storage.
 * Props:       FileUploadModalProps.
 * Behavior:    Validates type/size client-side, POSTs multipart, surfaces errors.
 * Data Source: remote (inherently-online upload); no Dexie writes here.
 * Constraints: file upload is the only allowed direct API call (see @fileoverview).
 * Performance: single-file; no virtualization.
 */
export default function FileUploadModal({
  isOpen,
  onClose,
  onSuccess,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  category: lockedCategory,
  title = 'Upload File',
  subtitle = 'Upload pamphlets, instructions, or other documents',
  showImagePreview = false,
  acceptHint,
}: FileUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(lockedCategory ?? 'other');
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const maxSizeLabel = `${Math.round(maxSizeBytes / (1024 * 1024))}MB`;

  // Revoke any outstanding image-preview object URL on unmount (covers the edge case
  // where the parent unmounts without routing through handleClose/resetForm).
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setName('');
    setDescription('');
    setCategory(lockedCategory ?? 'other');
    setStatus('idle');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [lockedCategory]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleFileSelect = useCallback((file: File) => {
    if (!allowedTypes.includes(file.type)) {
      setError(
        `File type "${file.type || 'unknown'}" is not allowed. Allowed: ${allowedTypes.join(', ')}.`,
      );
      return;
    }
    if (file.size > maxSizeBytes) {
      setError(`File size exceeds the ${maxSizeLabel} limit.`);
      return;
    }
    setSelectedFile(file);
    setName(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for display name
    setError(null);
    if (showImagePreview && file.type.startsWith('image/')) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    }
  }, [allowedTypes, maxSizeBytes, maxSizeLabel, showImagePreview]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setStatus('uploading');
    setError(null);

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', name || selectedFile.name);
      formData.append('description', description);
      formData.append('category', lockedCategory ?? category);

      // Upload to backend API using apiPostFormData (handles auth headers)
      const uploadedFile = await apiPostFormData<any>('/api/files/upload', formData);

      // Backend creates MongoDB record, replication will sync to Dexie automatically
      // No need to manually create Dexie record here (would create duplicate)

      setStatus('success');
      
      // Notify parent and close after brief delay
      setTimeout(() => {
        onSuccess?.(uploadedFile);
        handleClose();
      }, 1000);
    } catch (err: any) {
      console.error('[FileUploadModal] Upload failed:', err);
      setStatus('error');
      setError(err.message || 'Upload failed');
    }
  }, [selectedFile, name, description, category, lockedCategory, onSuccess, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="file-upload-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Upload className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 id="file-upload-modal-title" className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-blue-500 bg-blue-50'
                : selectedFile
                ? 'border-green-300 bg-green-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedTypes.join(',')}
              onChange={handleInputChange}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex flex-col items-center gap-2">
                {showImagePreview && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Logo preview"
                    className="max-h-24 max-w-[12rem] object-contain rounded border border-gray-200 bg-white"
                  />
                ) : (
                  <FileUp className="h-10 w-10 text-green-500" />
                )}
                <div className="font-medium text-gray-900">{selectedFile.name}</div>
                <div className="text-sm text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetForm();
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Choose different file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-10 w-10 text-gray-400" />
                <div className="font-medium text-gray-700">Drop file here or click to browse</div>
                <div className="text-sm text-gray-500">{acceptHint ?? `PDF, images, Word, Excel (max ${maxSizeLabel})`}</div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Success Message */}
          {status === 'success' && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <CheckCircle className="h-4 w-4 shrink-0" />
              File uploaded successfully!
            </div>
          )}

          {/* Form Fields */}
          {selectedFile && status !== 'success' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a display name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {!lockedCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleClose}
            disabled={status === 'uploading'}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || status === 'uploading' || status === 'success'}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'uploading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
